// rune-detail.ts — shared "big preview" chrome for the rune-icon bundle.
//
// Extracted verbatim from the admin lab (admin_tools/rune_icon_preview.php) so the lab
// and any live surface (e.g. the side info panel) render the same large rune + hover-to-
// identify readout from a single source. The lab calls renderDetail(host, ir,
// {legend:true, sizes:true}); the side panel calls it with {legend:false, sizes:false}.
//
// The renderer primitives (compile / renderSigil / status + label helpers) stay in
// rune-icon-engine.ts — this file only assembles them into the preview DOM.

import {
    renderSigil,
    compileStatus,
    dmg,
    durLabel,
    TYPE_LABEL,
    CAST_LABEL,
} from './rune-icon-engine';
import type { RuneIR, RuneStatusEntry } from './rune-icon-engine';
import { ITEM_BASE } from './data';

const TYPE_LABELS: Record<string, string> = TYPE_LABEL;
const CAST_LABELS: Record<string, string> = CAST_LABEL;

export interface RuneDetailOpts {
    size?: number;      // big sigil size (default 220)
    legend?: boolean;   // render the full legend table (default false — minimal)
    sizes?: boolean;    // render the 96/64 sizes row (default false)
    meta?: boolean;     // render the "circle · cast · area" meta line (default true)
    typeLabel?: boolean; // render a plain item-type eyebrow under the name (default false)
    sats?: boolean;     // status satellites (default true)
    cdn?: boolean;      // real CDN damage/status icons (default true)
    gen?: boolean;      // generic-token tier (default true)
    lod?: number | string; // detail override (default '' → derived from size)
    apBadge?: string;   // AP-cost badge variant (default '' = off)
}

/* ===== hover/tap a part of a rune SVG -> readout (verbatim port of wireSvg) ===== */
export function wireHover(svg: SVGElement, infoEl: HTMLElement | null): void {
    let active: SVGElement | null = null;
    const show = (node: SVGElement): void => {
        if (active && active !== node) active.style.filter = '';
        active = node;
        node.style.filter = 'drop-shadow(0 0 2.5px #fff)';
        const lab = node.getAttribute('data-part') || '';
        const tag = node.getAttribute('data-tag') || '';
        const [k, ...rest] = lab.split(':');
        if (infoEl) infoEl.innerHTML = `<span class="tag">${tag || k}</span> <b>${rest.join(':').trim() || k}</b>`;
    };
    svg.style.touchAction = 'manipulation';
    svg.addEventListener('pointermove', (e: PointerEvent) => {
        const n = e.target instanceof Element ? e.target.closest<SVGElement>('[data-part]') : null;
        if (n) show(n);
    });
    svg.addEventListener('pointerdown', (e: PointerEvent) => {
        const n = e.target instanceof Element ? e.target.closest<SVGElement>('[data-part]') : null;
        if (n) { e.preventDefault(); show(n); }
    });
}

/* ===== big preview panel (port of the admin lab's showPreview body) ===== */
export function renderDetail(host: HTMLElement, ir: RuneIR, opts: RuneDetailOpts = {}): void {
    const opt = {
        sats: opts.sats !== false,
        cdn: opts.cdn !== false,
        gen: opts.gen !== false,
        lod: opts.lod ?? '',
        apBadge: opts.apBadge ?? '',
    };
    const bigSize = opts.size ?? 220;
    host.innerHTML = '';

    const big = document.createElement('div');
    big.className = 'rf-big';
    big.appendChild(renderSigil(ir, bigSize, opt));
    host.appendChild(big);

    const right = document.createElement('div');
    right.className = 'rf-pv-right';
    host.appendChild(right);

    const team = ir.sameTeam ? ' · allies' : ir.oppTeam ? ' · enemies' : '';
    const fam = ir.isSpell ? ((ir.sphereAll || []).join(' + ') || '—') : TYPE_LABELS[ir.itype];
    const h = document.createElement('h4');
    if (ir.id) {
        const a = document.createElement('a');
        a.href = ITEM_BASE + encodeURIComponent(ir.id);
        a.target = '_blank';
        a.rel = 'noopener';
        a.textContent = ir.name;
        a.title = 'Open item in new tab';
        h.appendChild(a);
    } else {
        h.textContent = ir.name;
    }
    right.appendChild(h);

    // Plain item-type eyebrow (e.g. "Spell", "Full Ability") — the minimal panel view
    // shows just name + type and lets the user hover the rune to learn everything else.
    if (opts.typeLabel) {
        const typeEl = document.createElement('div');
        typeEl.className = 'rf-pv-type';
        typeEl.textContent = ir.isSpell ? 'Spell' : (TYPE_LABELS[ir.itype] || ir.itype);
        right.appendChild(typeEl);
    }

    if (opts.meta !== false) {
        const meta = document.createElement('div');
        meta.className = 'rf-pv-meta';
        meta.textContent = `${ir.circle || fam} · ${CAST_LABELS[ir.cast] || ir.cast} · ${ir.aoe || 'single'}${team}`;
        right.appendChild(meta);
    }

    if (opts.sizes) {
        const sizes = document.createElement('div');
        sizes.className = 'rf-pv-sizes';
        [96, 64].forEach(sz => {
            const fig = document.createElement('div');
            const tile = document.createElement('div');
            tile.className = 'rf-tile';
            tile.appendChild(renderSigil(ir, sz, opt));
            fig.appendChild(tile);
            const cap = document.createElement('div');
            cap.className = 'rf-cap';
            cap.textContent = sz + 'px';
            fig.appendChild(cap);
            sizes.appendChild(fig);
        });
        right.appendChild(sizes);
    }

    if (opts.legend) {
        const stRow = (e: RuneStatusEntry): string => {
            const c = compileStatus(e.it);
            const removed = e.mode === 'removed';
            const verb = removed ? 'removes' : (c.boon ? 'grants' : 'inflicts');
            const col = removed ? '#9aa7c7' : (c.boon ? '#5fd28a' : '#ef5a5a');
            return `${c.name} <span style="color:${col}">(${e.who} · ${verb} · ${e.timing})</span>`;
        };
        const dmgRow = ir.damage.map((d: string) => `<span class="rf-swatch" style="background:${dmg(d).c}"></span>${d}`).join(' ');
        const diceStr = (ir.dice && ir.die) ? `${ir.dice}d${ir.die}${ir.mod ? (ir.mod > 0 ? '+' : '') + ir.mod : ''}` : (ir.mod ? `${ir.mod > 0 ? '+' : ''}${ir.mod}` : '—');
        const legend = document.createElement('div');
        legend.className = 'rf-legend';
        legend.innerHTML = `<table>
    <tr><td class="k">${ir.isSpell ? 'Sphere / Circle' : 'Ability type'}</td><td>${ir.isSpell ? `${ir.sphereAll.join(' + ') || '—'} · ${ir.circle || '—'}` : TYPE_LABELS[ir.itype]}</td></tr>
    <tr><td class="k">Damage</td><td>${ir.damage.length ? `${dmgRow} · ${diceStr}${ir.avg != null ? ` (total ${ir.avg})` : ''}` : '—'}</td></tr>
    <tr><td class="k">Roll / Range</td><td>${ir.roll || '—'}${ir.fixedDc != null ? ` (DC ${ir.fixedDc})` : ''} · ${ir.range || '—'}</td></tr>
    ${(ir.contesterSkills.length || ir.contesteeSkills.length) ? `<tr><td class="k">Contest</td><td><span style="color:#3b82f6">caster: ${ir.contesterSkills.join(', ') || '—'}</span> vs <span style="color:#ef5a5a">target: ${ir.contesteeSkills.join(', ') || '—'}</span>${ir.targetRetries ? ' · <span style="color:#ef5a5a">retries each EoT</span>' : ''}</td></tr>` : ''}
    ${ir.moveToward ? `<tr><td class="k">Charge</td><td>caster moves ${ir.moveToward}ft toward target</td></tr>` : ''}
    <tr><td class="k">Area</td><td>${ir.aoe ? ir.aoe + ' ' + (ir.aoeDist || '') + 'ft' : 'single target'}</td></tr>
    ${(ir.shortCaster || ir.shortTarget) ? `<tr><td class="k">Ends</td><td>${[ir.shortCaster ? 'caster turn' : null, ir.shortTarget ? 'target turn' : null].filter(Boolean).join(' / ')}</td></tr>` : ''}
    ${(ir.rechargeBegin || ir.resetsLong || ir.resetsShort) ? `<tr><td class="k">Recovery</td><td>${ir.rechargeBegin ? `recharge ${ir.rechargeBegin}${ir.rechargeEnd && ir.rechargeEnd !== ir.rechargeBegin ? '–' + ir.rechargeEnd : ''}` : ''}${ir.resetsLong ? 'long rest' : ''}${ir.resetsShort ? (ir.resetsLong ? ', short rest' : 'short rest') : ''}</td></tr>` : ''}
    <tr><td class="k">Cost / Duration</td><td>${CAST_LABELS[ir.cast] || ir.cast} · ${durLabel(ir)}</td></tr>
    <tr><td class="k">Targets</td><td>${ir.sameTeam ? 'allies only' : ir.oppTeam ? 'enemies only' : 'any'}</td></tr>
    ${ir.hasTrigger ? `<tr><td class="k">Triggers on</td><td>${ir.trigLabel}</td></tr>` : ''}
    ${ir.summon.length ? `<tr><td class="k">Summons</td><td>${ir.summon.join(', ')}</td></tr>` : ''}
    ${ir.fabricate.length ? `<tr><td class="k">Fabricates</td><td>${ir.fabricate.join(', ')}</td></tr>` : ''}
    ${ir.consumesCharges != null ? `<tr><td class="k">Charges</td><td><span style="color:#ef5a5a">consumes ${ir.consumesCharges}${ir.consumesName ? ' of ' + ir.consumesName : ''}</span></td></tr>` : ''}
    ${ir.addsCharges != null ? `<tr><td class="k">Charges</td><td><span style="color:#34d399">grants ${ir.addsCharges}${ir.addsName ? ' to ' + ir.addsName : ''}</span></td></tr>` : ''}
    ${(ir.light || ir.destroysLight) ? `<tr><td class="k">Light</td><td>${ir.destroysLight ? 'destroys light' : (ir.light === 'dim' ? 'creates dim light' : 'creates bright light')}</td></tr>` : ''}
    ${ir.condition ? `<tr><td class="k">Condition</td><td>${ir.condition}</td></tr>` : ''}
    ${ir.disarmed ? `<tr><td class="k">Disarm</td><td><span style="color:#ef5a5a">disarms target</span></td></tr>` : ''}
    ${ir.resist.length ? `<tr><td class="k">Resist</td><td>${ir.resist.map((d: string) => `<span class="rf-swatch" style="background:${dmg(d).c}"></span>${d}`).join(' ')}</td></tr>` : ''}
    ${ir.immune.length ? `<tr><td class="k">Immune</td><td>${ir.immune.map((d: string) => `<span class="rf-swatch" style="background:${dmg(d).c}"></span>${d}`).join(' ')}</td></tr>` : ''}
    ${ir.vuln.length ? `<tr><td class="k">Vulnerable</td><td>${ir.vuln.map((d: string) => `<span class="rf-swatch" style="background:${dmg(d).c}"></span>${d}`).join(' ')}</td></tr>` : ''}
    ${ir.apChange ? `<tr><td class="k">AP change</td><td>${ir.apChange === 'r' ? 'reaction reset' : 'AP ' + (parseInt(ir.apChange, 10) > 0 ? '+' : '') + ir.apChange + ' during turn'}</td></tr>` : ''}
    ${ir.grantedAbilities && ir.grantedAbilities.length ? `<tr><td class="k">Grants ability</td><td>${ir.grantedAbilities.map((a) => `${a.name} <span style="color:var(--rf-muted)">(${TYPE_LABELS[a.type] || a.type})</span>`).join('<br>')}</td></tr>` : ''}
    ${ir.skillAdv ? `<tr><td class="k">Skill roll</td><td><span style="color:#34d399">ADV${ir.skillAdvNames.length ? ' (' + ir.skillAdvNames.join(', ') + ')' : ''}</span></td></tr>` : ''}
    ${ir.skillDis ? `<tr><td class="k">Skill roll</td><td><span style="color:#ef5a5a">DIS${ir.skillDisNames.length ? ' (' + ir.skillDisNames.join(', ') + ')' : ''}</span></td></tr>` : ''}
    ${ir.rollManip ? `<tr><td class="k">Roll manip</td><td>${ir.rollManip}</td></tr>` : ''}
    ${ir.statusEntries.length ? `<tr><td class="k">Status effects</td><td>${ir.statusEntries.map(stRow).join('<br>')}</td></tr>` : ''}
  </table>`;
        right.appendChild(legend);
    }

    const info = document.createElement('div');
    info.className = 'rf-info';
    info.innerHTML = '<span class="tag">hover</span> point at any part of the big rune to identify it…';
    right.appendChild(info);

    wireHover(big.querySelector('svg') as SVGElement, info);
}
